import React, { useState, useEffect } from 'react';
import { COLORS, SPACING } from '../styles/theme';
import { Post, deletePost, forceDeletePost, pinPost, unpinPost } from '../lib/posts';
import { recordSignal } from '../lib/signals';
import { asText, pubkeyShortHash, linkifyText } from '../lib/sanitize';
import { PostComposer } from './PostComposer';
import { getLikeCount, hasLiked, addLikesListener } from '../lib/likes';
import { publishEvent } from '../lib/sync';
import { loadOrCreateIdentity } from '../lib/identity';
import { buildPostLink } from '../lib/deepLink';
import { getProfile, getRemoteProfileByPubkey, calculateTastemakerScore } from '../lib/metadata';
import { MediaAttachment } from './MediaAttachment';
import { ReportButton } from './ReportButton';
import { isAdmin } from '../lib/moderation';

const MAX_DISPLAY_DEPTH = 3;

interface Props {
  post: Post;
  allPosts: Post[];
  depth?: number;
  isPinned?: boolean;
  onOpenThread?: (postId: string) => void;
  onReloadFeed?: () => void;
}

function authorDisplayName(pubkey: string, myPublicKey: string): string {
  const profile = pubkey === myPublicKey ? getProfile() : getRemoteProfileByPubkey(pubkey);
  if (profile?.displayPreference === 'username' && profile.username?.trim()) {
    return asText(profile.username);
  }
  return pubkeyShortHash(pubkey);
}

function timeAgo(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`;
  return `${Math.floor(diff / 86_400_000)}d`;
}

export function PostCard({ post, allPosts, depth = 0, isPinned = false, onOpenThread, onReloadFeed }: Props) {
  const [showReply, setShowReply] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [shared, setShared] = useState(false);
  const identity = loadOrCreateIdentity();
  const admin = isAdmin(identity.publicKey);
  const authorProfile = post.pubkey === identity.publicKey ? getProfile() : getRemoteProfileByPubkey(post.pubkey);
  const authorScore = authorProfile ? calculateTastemakerScore(authorProfile.userId) : 0;
  const [likeCount, setLikeCount] = useState(() => getLikeCount(post.id));
  const [liked, setLiked] = useState(() => hasLiked(post.id, identity.userId));

  useEffect(() => {
    return addLikesListener(() => {
      setLikeCount(getLikeCount(post.id));
      setLiked(hasLiked(post.id, identity.userId));
    });
  }, [post.id, identity.userId]);

  const directReplies = allPosts.filter(p => p.replyTo?.postId === post.id);
  const isCollapsed = depth >= MAX_DISPLAY_DEPTH && !expanded && directReplies.length > 0;

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const handleLike = (e: React.MouseEvent) => {
    stop(e);
    if (liked) return;
    recordSignal(post.id, 'like', post.magnetURI);
    publishEvent('like', { targetId: post.id, targetType: 'post', userId: identity.userId });
    onReloadFeed?.();
  };

  const handleDislike = (e: React.MouseEvent) => {
    stop(e);
    recordSignal(post.id, 'dislike', post.magnetURI);
    onReloadFeed?.();
  };

  const handleShare = (e: React.MouseEvent) => {
    stop(e);
    recordSignal(post.id, 'share', post.magnetURI);
    navigator.clipboard.writeText(buildPostLink(post.id)).catch(() => {});
    setShared(true);
    setTimeout(() => setShared(false), 1500);
  };

  const handleReply = (e: React.MouseEvent) => {
    stop(e);
    setShowReply(v => !v);
  };

  const isOwn = post.pubkey === identity.publicKey;

  const handleDelete = async (e: React.MouseEvent) => {
    stop(e);
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    deletePost(post.id, identity.publicKey);
    await publishEvent('delete_post', { postId: post.id, pubkey: identity.publicKey });
    onReloadFeed?.();
  };

  const handleAdminDelete = async (e: React.MouseEvent) => {
    stop(e);
    if (!window.confirm('Admin delete this post? This cannot be undone.')) return;
    forceDeletePost(post.id);
    await publishEvent('delete_post', { postId: post.id, pubkey: identity.publicKey });
    onReloadFeed?.();
  };

  const handlePin = async (e: React.MouseEvent) => {
    stop(e);
    pinPost(post.id);
    await publishEvent('pin_post', { postId: post.id, pubkey: identity.publicKey });
    onReloadFeed?.();
  };

  const handleUnpin = async (e: React.MouseEvent) => {
    stop(e);
    unpinPost(post.id);
    await publishEvent('unpin_post', { postId: post.id, pubkey: identity.publicKey });
    onReloadFeed?.();
  };

  const renderContent = () => {
    const safe = asText(post.content);
    return linkifyText(safe).map((seg, i) =>
      seg.type === 'url'
        ? <a key={i} href={seg.content} target="_blank" rel="noopener noreferrer"
             onClick={stop} style={{ color: COLORS.highlight }}>{seg.content}</a>
        : <span key={i}>{seg.content}</span>
    );
  };

  const indentStyle: React.CSSProperties = depth > 0 ? {
    marginLeft: SPACING.xl,
    paddingLeft: SPACING.md,
    borderLeft: `2px solid ${COLORS.border}`,
  } : {};

  if (isCollapsed) {
    return (
      <div style={indentStyle}>
        <button
          onClick={e => { stop(e); setExpanded(true); }}
          style={{ background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '11px', padding: `${SPACING.xs} 0`, fontFamily: '"IBM Plex Mono", monospace' }}
        >
          ▸ {directReplies.length} {directReplies.length === 1 ? 'reply' : 'replies'}
        </button>
      </div>
    );
  }

  return (
    <div style={indentStyle}>
      <div style={{ padding: SPACING.md, borderBottom: `1px solid ${COLORS.border}` }}>
        <div style={{ display: 'flex', gap: SPACING.sm, marginBottom: SPACING.xs, fontSize: '11px', color: COLORS.textMuted, fontFamily: '"IBM Plex Mono", monospace', alignItems: 'center' }}>
          {isPinned && <span style={{ color: COLORS.accent }}>📌</span>}
          <span style={{ color: COLORS.highlight, fontWeight: 600 }}>{authorDisplayName(post.pubkey, identity.publicKey)}</span>
          {authorScore > 0 && <span style={{ color: COLORS.accent, fontSize: '10px' }}>★ {authorScore}</span>}
          <span>·</span>
          <span>{timeAgo(post.timestamp)}</span>
          {post.city && <><span>·</span><span>{post.city}</span></>}
        </div>

        <div
          style={{ fontSize: '14px', lineHeight: '1.6', wordBreak: 'break-word', marginBottom: SPACING.sm, cursor: onOpenThread ? 'pointer' : 'default', fontFamily: '"IBM Plex Mono", monospace' }}
          onClick={() => onOpenThread?.(post.id)}
        >
          {renderContent()}
        </div>

        {post.magnetURI && (
          <div style={{ marginBottom: SPACING.sm }}>
            <MediaAttachment magnetURI={post.magnetURI} attachmentType={post.mediaType} />
          </div>
        )}

        <div style={{ display: 'flex', gap: SPACING.lg, fontSize: '11px' }} onClick={stop}>
          <button onClick={handleLike} style={{ background: 'none', border: 'none', color: liked ? COLORS.accent : COLORS.textMuted, cursor: liked ? 'default' : 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>♥ {likeCount > 0 ? likeCount : 'like'}</button>
          <button onClick={handleDislike} style={{ background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>✕ dislike</button>
          <button onClick={handleReply}   style={{ background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>↩ reply</button>
          <button onClick={handleShare}   style={{ background: 'none', border: 'none', color: shared ? COLORS.highlight : COLORS.textMuted, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>{shared ? '✓ copied' : '⇥ share'}</button>
          {isOwn && (
            <button onClick={handleDelete} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>🗑 delete</button>
          )}
          {!isOwn && admin && (
            <button onClick={handleAdminDelete} style={{ background: 'none', border: 'none', color: COLORS.danger, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>🗑 admin delete</button>
          )}
          {!isOwn && <ReportButton targetId={post.id} targetType="post" />}
          {admin && !isPinned && depth === 0 && (
            <button onClick={handlePin} style={{ background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>📌 pin</button>
          )}
          {admin && isPinned && depth === 0 && (
            <button onClick={handleUnpin} style={{ background: 'none', border: 'none', color: COLORS.accent, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>📌 unpin</button>
          )}
          {onOpenThread && (
            <button onClick={e => { stop(e); onOpenThread(post.id); }} style={{ background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}>
              ↗ thread
            </button>
          )}
        </div>

        {showReply && (
          <div onClick={stop}>
            <PostComposer
              replyTo={{ pubkey: post.pubkey, postId: post.id }}
              onPosted={() => { setShowReply(false); onReloadFeed?.(); }}
            />
          </div>
        )}
      </div>

      {directReplies.map(r => (
        <PostCard
          key={r.id}
          post={r}
          allPosts={allPosts}
          depth={depth + 1}
          onOpenThread={onOpenThread}
          onReloadFeed={onReloadFeed}
        />
      ))}
    </div>
  );
}
